import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { LayoutGrid, List as ListIcon, Table as TableIcon, BarChart3, Clock, ChevronRight } from "lucide-react";
import { formatDuration } from "@/lib/format";
import KanbanView from "@/components/views/KanbanView";
import ListView from "@/components/views/ListView";
import TableView from "@/components/views/TableView";
import GanttView from "@/components/views/GanttView";
import TaskSheet from "@/components/TaskSheet";
import FilterBar from "@/components/FilterBar";
import { toast } from "sonner";

const DEFAULT_FILTERS = { q: "", status: "any", priority: "any", assignee: "any", due: "any", time: "any" };

export default function ListPage() {
  const { listId } = useParams();
  const { tree, activeWorkspace, users } = useWorkspace();
  const [list, setList] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem("orbit_view") || "list");
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem("orbit_group_by") || "status");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [openTask, setOpenTask] = useState({ open: false, taskId: null });
  const [summary, setSummary] = useState(null);

  const refresh = useCallback(async () => {
    if (!listId) return;
    try {
      const [{ data: l }, { data: t }, { data: s }] = await Promise.all([
        api.get(`/lists/${listId}`),
        api.get(`/lists/${listId}/tasks`),
        api.get(`/lists/${listId}/time-summary`),
      ]);
      setList(l.list);
      setTasks(t.tasks);
      setSummary(s);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load list");
    }
  }, [listId]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { setFilters(DEFAULT_FILTERS); }, [listId]);

  const setViewPersist = (v) => { setView(v); localStorage.setItem("orbit_view", v); };
  const setGroupPersist = (v) => { setGroupBy(v); localStorage.setItem("orbit_group_by", v); };

  const quickAdd = async (title, overrides = {}) => {
    try {
      const { data } = await api.post(`/lists/${listId}/tasks`, { title, ...overrides });
      setTasks((prev) => [...prev, data.task]);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create task");
    }
  };

  const onUpdateTask = async (taskId, patch) => {
    try {
      const { data } = await api.patch(`/tasks/${taskId}`, patch);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? data.task : t)));
      refresh();
      return data.task;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
      throw e;
    }
  };

  const onDeleteTask = async (taskId) => {
    try {
      await api.delete(`/tasks/${taskId}`);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      toast.success("Task deleted");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const folder = useMemo(() => {
    if (!list) return null;
    return tree.folders.find((f) => f.id === list.folder_id) || null;
  }, [list, tree.folders]);

  const myRole = list?.my_role || "viewer";
  const canEdit = ["admin", "member"].includes(myRole);

  if (!list) {
    return <div className="p-10 font-mono-data text-xs uppercase tracking-widest text-neutral-500" data-testid="list-loading">Loading list…</div>;
  }

  const statuses = (list.statuses || []).slice().sort((a, b) => a.order - b.order);
  const openTaskById = (id) => setOpenTask({ open: true, taskId: id });

  return (
    <div data-testid="list-page">
      <header className="h-14 border-b border-neutral-200 flex items-center px-6 bg-white/80 backdrop-blur-xl sticky top-0 z-20">
        <div className="min-w-0">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-1">
            <Link to="/" className="hover:text-neutral-950">{activeWorkspace?.name}</Link>
            <ChevronRight className="w-3 h-3" />
            {folder?.name || "Folder"}
          </div>
          <h1 className="font-display text-base font-bold leading-none truncate">{list.name}</h1>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 px-3 h-9 border border-neutral-200 rounded-sm bg-white">
            <Clock className="w-3.5 h-3.5 text-neutral-500" />
            <span className="font-mono-data text-xs">{formatDuration(summary?.total_seconds || 0)}</span>
            <span className="font-mono-data text-[10px] uppercase tracking-widest text-neutral-500">tracked</span>
          </div>

          <div className="flex border border-neutral-200 rounded-sm bg-white overflow-hidden">
            {[
              { k: "list", icon: ListIcon, label: "List" },
              { k: "kanban", icon: LayoutGrid, label: "Kanban" },
              { k: "table", icon: TableIcon, label: "Table" },
              { k: "gantt", icon: BarChart3, label: "Gantt" },
            ].map(({ k, icon: Icon, label }) => (
              <button
                key={k}
                onClick={() => setViewPersist(k)}
                className={`flex items-center gap-1.5 px-3 h-9 text-xs font-semibold transition-colors ${
                  view === k ? "bg-neutral-950 text-white" : "text-neutral-700 hover:bg-neutral-50"
                }`}
                data-testid={`view-toggle-${k}`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="hidden md:inline">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      <FilterBar
        filters={filters}
        onChange={setFilters}
        groupBy={groupBy}
        onGroupBy={setGroupPersist}
        statuses={statuses}
        users={users}
        canEdit={canEdit}
        onQuickAdd={quickAdd}
      />

      <div className="p-6">
        {view === "kanban" && (
          <KanbanView list={list} tasks={tasks} users={users} filters={filters} canEdit={canEdit}
            onOpenTask={openTaskById} onUpdateTask={onUpdateTask} />
        )}
        {view === "list" && (
          <ListView list={list} tasks={tasks} users={users} filters={filters} groupBy={groupBy} canEdit={canEdit}
            onOpenTask={openTaskById} onUpdateTask={onUpdateTask}
            onQuickAdd={quickAdd} onRefresh={refresh} />
        )}
        {view === "table" && (
          <TableView list={list} tasks={tasks} users={users} filters={filters} canEdit={canEdit}
            onOpenTask={openTaskById} onUpdateTask={onUpdateTask} onRefresh={refresh} />
        )}
        {view === "gantt" && (
          <GanttView list={list} tasks={tasks} users={users} filters={filters} canEdit={canEdit}
            onOpenTask={openTaskById} onUpdateTask={onUpdateTask} />
        )}
      </div>

      <TaskSheet
        open={openTask.open}
        taskId={openTask.taskId}
        list={list}
        users={users}
        allTasks={tasks}
        canEdit={canEdit}
        onOpenChange={(v) => setOpenTask((s) => ({ ...s, open: v }))}
        onChange={refresh}
        onDelete={onDeleteTask}
        onOpenTask={openTaskById}
      />
    </div>
  );
}
